import { z } from 'zod'
import { auditActorTypeSchema } from '../enums/audit-actor-type.enum.js'
import { auditEventTypeSchema } from '../enums/audit-event-type.enum.js'
import { envelopeCreatedViaSchema } from '../enums/envelope-created-via.enum.js'
import { envelopeStatusSchema } from '../enums/envelope-status.enum.js'
import { signerStatusSchema } from '../enums/signer-status.enum.js'
import { signerDeliverySchema } from '../enums/signer-delivery.enum.js'
import { documentSourceSchema } from '../enums/document-source.enum.js'
import { tenantModeSchema } from '../enums/tenant-mode.enum.js'
import { stringRecordSchema } from '../helpers/json.js'
import { uuidSchema } from '../validators/params.js'

export const fieldOptionDtoSchema = z.object({
  key: z.string(),
  label: z.string(),
})

export type FieldOptionDto = z.infer<typeof fieldOptionDtoSchema>

/**
 * A drawn signature is a PNG or JPEG data URL. Typed ink is plain text and
 * uses the other arm. The PDF renderer decodes this same pattern.
 */
export const signatureDrawDataPattern =
  /^data:image\/(png|jpeg|jpg);base64,([A-Za-z0-9+/=]+)$/i

const signatureDrawDataSchema = z
  .string()
  .trim()
  .max(100_000)
  .regex(signatureDrawDataPattern, 'Use a PNG or JPEG data URL')

export const signatureValueDtoSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('draw'),
    data: signatureDrawDataSchema,
  }),
  z.object({
    kind: z.literal('type'),
    data: z.string().max(100_000),
  }),
])

export type SignatureValueDto = z.infer<typeof signatureValueDtoSchema>

/**
 * A value stored for one field. Text, date, date-signed, radio, and dropdown
 * are strings; a checkbox is a boolean; a signature or initials is
 * {@link SignatureValueDto}. A number is not a field value: no field type
 * accepts one, and accepting it here let it count as filled and then get dropped.
 */
export const fieldValueDtoSchema = z.union([
  z.string().max(4000),
  z.boolean(),
  signatureValueDtoSchema,
])

export type FieldValueDto = z.infer<typeof fieldValueDtoSchema>

export const notifyCopyDtoSchema = z.object({
  subject: z.string().nullable(),
  message: z.string().nullable(),
})

export type NotifyCopyDto = z.infer<typeof notifyCopyDtoSchema>

export const envelopeNotifyDtoSchema = z.object({
  remind: notifyCopyDtoSchema,
})

export type EnvelopeNotifyDto = z.infer<typeof envelopeNotifyDtoSchema>

export const signerNotifyDtoSchema = z.object({
  invite: notifyCopyDtoSchema,
  remind: notifyCopyDtoSchema,
})

export type SignerNotifyDto = z.infer<typeof signerNotifyDtoSchema>

export const documentDtoSchema = z.object({
  id: uuidSchema,
  clientKey: z.string(),
  filename: z.string(),
  title: z.string(),
  source: documentSourceSchema,
  presentationOrder: z.number().int().positive(),
  pageCount: z.number().int().positive(),
  byteSize: z.number().int().nonnegative(),
})

export type DocumentDto = z.infer<typeof documentDtoSchema>

export const answererDtoSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  email: z.string().email(),
  relationship: z.string(),
  signerId: uuidSchema.nullable(),
  /** The answerer's Q&A link, only in the create response (links are stored hashed and shown once); null otherwise. */
  qaUrl: z.string().url().nullable(),
  consentAcceptedAt: z.string().datetime().nullable(),
})

export type AnswererDto = z.infer<typeof answererDtoSchema>

export const signerDtoSchema = z.object({
  id: uuidSchema,
  clientKey: z.string(),
  name: z.string(),
  email: z.string().email(),
  role: z.string(),
  routingOrder: z.number().int().positive(),
  status: signerStatusSchema,
  /**
   * The signer's own deadline, earlier than the envelope's: if they haven't
   * finished by then, the envelope expires. Null when they have the envelope's.
   */
  expiresAt: z.string().datetime().nullable(),
  /** `caller`: the caller hands this signer their link, and PixelSign emails them only the completed copy. */
  delivery: signerDeliverySchema,
  /**
   * The signer's links, only in the response of the call that issued them
   * (create or send for the first signers, remind, or
   * POST /v1/envelopes/{id}/signers/{signerId}/links): they're stored hashed
   * and shown once. Null in every other response.
   */
  signingUrl: z.string().url().nullable(),
  embedUrl: z.string().url().nullable(),
  redirectUrl: z.string().url().nullable(),
  notify: signerNotifyDtoSchema,
  sentAt: z.string().datetime().nullable(),
  viewedAt: z.string().datetime().nullable(),
  signedAt: z.string().datetime().nullable(),
  declinedAt: z.string().datetime().nullable(),
  declineReason: z.string().nullable(),
  /**
   * The signer declined on the consent screen, before agreeing to sign
   * electronically, rather than declining the documents: offer them paper.
   */
  declinedConsent: z.boolean(),
  consentVersion: z.string().nullable(),
  consentAcceptedAt: z.string().datetime().nullable(),
  /** When the signer last asked for a paper copy, which the organization mails them. Null if they haven't. */
  paperCopyRequestedAt: z.string().datetime().nullable(),
  /** What they wrote with that request, such as where to send it. */
  paperCopyNote: z.string().nullable(),
  questionsEnabled: z.boolean(),
  questionsAnswerer: answererDtoSchema.nullable(),
})

export type SignerDto = z.infer<typeof signerDtoSchema>

/**
 * A freshly issued signing link and its embed form. Issuing one replaces the
 * signer's previous link, which keeps working for an hour.
 */
export const signerLinksDtoSchema = z.object({
  signerId: uuidSchema,
  signingUrl: z.string().url(),
  embedUrl: z.string().url(),
})

export type SignerLinksDto = z.infer<typeof signerLinksDtoSchema>

const fieldBoxShape = {
  id: uuidSchema,
  name: z.string(),
  documentId: uuidSchema,
  signerId: uuidSchema,
  signerName: z.string().min(1),
  page: z.number().int().positive(),
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  required: z.boolean(),
  readOnly: z.boolean(),
  group: z.string().nullable(),
  maxLength: z.number().int().positive().nullable(),
}

export const fieldDtoSchema = z.discriminatedUnion('type', [
  z.object({
    ...fieldBoxShape,
    type: z.literal('checkbox'),
    value: z.boolean().nullable(),
    options: z.null(),
  }),
  z.object({
    ...fieldBoxShape,
    type: z.literal('signature'),
    value: signatureValueDtoSchema.nullable(),
    options: z.null(),
  }),
  z.object({
    ...fieldBoxShape,
    type: z.literal('initials'),
    value: signatureValueDtoSchema.nullable(),
    options: z.null(),
  }),
  z.object({
    ...fieldBoxShape,
    type: z.literal('radio'),
    value: z.string().nullable(),
    options: z.null(),
  }),
  z.object({
    ...fieldBoxShape,
    type: z.literal('dropdown'),
    value: z.string().nullable(),
    options: z.array(fieldOptionDtoSchema).nullable(),
  }),
  z.object({
    ...fieldBoxShape,
    type: z.literal('text'),
    value: z.string().nullable(),
    options: z.null(),
  }),
  z.object({
    ...fieldBoxShape,
    type: z.literal('date'),
    value: z.string().nullable(),
    options: z.null(),
  }),
  z.object({
    ...fieldBoxShape,
    type: z.literal('date_signed'),
    value: z.string().nullable(),
    options: z.null(),
  }),
])

export type FieldDto = z.infer<typeof fieldDtoSchema>

export const auditEventDtoSchema = z.object({
  id: uuidSchema,
  type: auditEventTypeSchema,
  actorType: auditActorTypeSchema,
  actorId: z.string().nullable(),
  signerId: uuidSchema.nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  payload: stringRecordSchema.nullable(),
  createdAt: z.string().datetime(),
})

export type AuditEventDto = z.infer<typeof auditEventDtoSchema>

/**
 * Who created the envelope. `source` is `mcp` whenever it came through an AI
 * app over MCP: a person (`person`) or an API key used from an AI app
 * (`apiKey` plus `app`). Absent from responses and webhook replays recorded
 * before October 2026.
 */
export const envelopeCreatorDtoSchema = z.object({
  source: envelopeCreatedViaSchema,
  apiKey: z.object({ id: uuidSchema, name: z.string(), prefix: z.string() }).nullable(),
  person: z.object({ name: z.string(), email: z.string() }).nullable(),
  /** The AI app it came through, as the app names itself. */
  app: z.string().nullable(),
})

export type EnvelopeCreatorDto = z.infer<typeof envelopeCreatorDtoSchema>

export const envelopeDtoSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  emailSubject: z.string(),
  emailMessage: z.string(),
  status: envelopeStatusSchema,
  mode: tenantModeSchema,
  expiresAt: z.string().datetime(),
  remindEveryDays: z.number().int().positive(),
  remindMax: z.number().int().positive(),
  notify: envelopeNotifyDtoSchema,
  /** Days of retention after the envelope finishes. Null means indefinite. */
  retentionDays: z.number().int().positive().nullable(),
  /** When the files will be purged. Null while the envelope is still in flight. */
  purgeAt: z.string().datetime().nullable(),
  metadata: stringRecordSchema,
  webhookUrl: z.string().url().nullable(),
  sentAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  declinedAt: z.string().datetime().nullable(),
  voidedAt: z.string().datetime().nullable(),
  expiredAt: z.string().datetime().nullable(),
  voidReason: z.string().nullable(),
  declineReason: z.string().nullable(),
  documents: z.array(documentDtoSchema),
  signers: z.array(signerDtoSchema),
  answerers: z.array(answererDtoSchema),
  fields: z.array(fieldDtoSchema),
  createdBy: envelopeCreatorDtoSchema.optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export type EnvelopeDto = z.infer<typeof envelopeDtoSchema>

export const envelopeListItemDtoSchema = envelopeDtoSchema.omit({
  fields: true,
})

export type EnvelopeListItemDto = z.infer<typeof envelopeListItemDtoSchema>

export const envelopeListDtoSchema = z.object({
  envelopes: z.array(envelopeListItemDtoSchema),
  total: z.number().int().nonnegative(),
})

export type EnvelopeListDto = z.infer<typeof envelopeListDtoSchema>

export const envelopeAuditListDtoSchema = z.object({
  events: z.array(auditEventDtoSchema),
})

export type EnvelopeAuditListDto = z.infer<typeof envelopeAuditListDtoSchema>
